import SearchFlightsResultModalRange from "./SearchFlightsResultModalRange";
import SearchFlightsResultModalFlight from "./SearchFlightsResultModalFlight";
import React, { useEffect, useState } from "react";
import { FlightDetails, RMFlightWrapper, Mapper } from "../../../types";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import { selectChosenFlightsResults } from "../searchFlightsSlice";
import { Locale } from "date-fns";
import { getLocale } from "../../../utils";
import { registerLocale } from "react-datepicker";

function SearchFlightsResultModalDetails({
  result,
  mapper,
}: {
  result: RMFlightWrapper;
  mapper: Mapper;
}) {
  const { t } = useTranslation();

  const [locale, setLocale] = useState<Locale>();

  const language = useAppSelector((state) => state.config.app.language);

  const { dateMin, dateMax } = useAppSelector(selectChosenFlightsResults);
  const dateRange = useAppSelector(
    (state) => state.searchFlights.form.dateRange
  );
  const dateRangeWeekly = useAppSelector(
    (state) => state.searchFlights.form.dateRangeWeekly
  );

  const [flightDetail1, setFlightDetails1] = useState<FlightDetails>();
  const [flightDetail2, setFlightDetails2] = useState<FlightDetails>();
  const [flightDetail3, setFlightDetails3] = useState<FlightDetails>();

  useEffect(() => {
    const newLocale = getLocale(language);
    setLocale(newLocale);
    registerLocale(language, newLocale);
  }, [language]);

  useEffect(() => {
    if (result) {
      setFlightDetails1(convertFlightsDetail(result, 1));
      setFlightDetails2(convertFlightsDetail(result, 2));
      setFlightDetails3(convertFlightsDetail(result, 3));
    } else {
      setFlightDetails1(null);
      setFlightDetails2(null);
      setFlightDetails3(null);
    }
  }, [result]);

  if (!flightDetail1) {
    return null;
  }

  return (
    ((dateRange || dateRangeWeekly) && (
      <SearchFlightsResultModalRange
        flightDetails1={flightDetail1}
        flightDetails2={flightDetail2}
        flightDetails3={flightDetail3}
        dateMin={dateMin}
        dateMax={dateMax}
      />
    )) || (
      <>
        {flightDetail1 !== null && (
          <SearchFlightsResultModalFlight
            flightDetails={flightDetail1}
            mapper={mapper}
            locale={locale}
          />
        )}
        {flightDetail2 !== null && (
          <>
            <h6 className="subtitle is-6">
              {t("LABEL_FLIGHT_FINDER_CONNECTING_TO")}
            </h6>
            <SearchFlightsResultModalFlight
              flightDetails={flightDetail2}
              mapper={mapper}
              locale={locale}
            />
          </>
        )}
        {flightDetail3 !== null && (
          <>
            <h6 className="subtitle is-6">
              {t("LABEL_FLIGHT_FINDER_CONNECTING_TO")}
            </h6>
            <SearchFlightsResultModalFlight
              flightDetails={flightDetail3}
              mapper={mapper}
              locale={locale}
            />
          </>
        )}
        {result.travelTime !== result.totalTravelTime && (
          <>
            <hr />
            <div className="columns is-mobile">
              <div className="column is-narrow">
                <span className="label">
                  {t("LABEL_FLIGHT_FINDER_TOTAL_JOURNEY")}
                </span>
              </div>
              <div className="column">
                <span>
                  {`${Math.floor(result.totalTravelTime / 60)}h ${
                    result.totalTravelTime % 60
                  }m`}
                </span>
              </div>
            </div>
          </>
        )}
      </>
    )
  );
}

function convertFlightsDetail(
  result: RMFlightWrapper,
  leg: number
): FlightDetails {
  if (!result) return null;
  if (leg === 1) {
    return {
      aircraftName: result.aircraftName,
      airline: result.airline,
      arrival: result.connxLeg1Arrival || result.arrival,
      arrivalName: result.connxLeg1ArrivalName || result.arrivalName,
      arrivalPortName:
        result.connxLeg1ArrivalPortName || result.arrivalPortName,
      arrivalTerminal:
        result.connxLeg1ArrivalTerminal || result.arrivalTerminal,
      arrivalDate: result.connxLeg1ArrivalDate || result.arrivalDate,
      arrivalDateAsString:
        result.connxLeg1ArrivalDateAsString || result.arrivalDateAsString,
      arrivalTimeAsString:
        result.connxLeg1ArrivalTimeAsString || result.arrivalTimeAsString,
      classBusiness: result.classesBusiness,
      classEconomy: result.classesEconomy,
      classFirst: result.classesFirst,
      mealsBusiness: result.mealsBusiness,
      mealsEconomy: result.mealsEconomy,
      mealsFirst: result.mealsFirst,
      depart: result.depart,
      departName: result.departName,
      departPortName: result.departPortName,
      departureTerminal: result.departureTerminal,
      departureDate: result.departureDate,
      departureDateAsString: result.departureDateAsString,
      departureTimeAsString: result.departureTimeAsString,
      effectiveFrom: result.effectiveFrom,
      effectiveTo: result.effectiveTo,
      flightNo: result.flightNo,
      operatedBy: result.operatedBy,
      travelTime: result.travelTime,
      oagOpByFlightNo: result.oagOpByFlightNo,
      oagOpByCarrier: result.oagOpByCarrier,
      sadName: result.oagOpByCarrierName || result.sadName,
      frequency: result.frequency,
    };
  } else if (leg === 2) {
    if (result.connxLeg2OperatedBy === null) return null;
    return {
      aircraftName: result.connxLeg2AircraftName,
      airline: result.connxLeg2Airline,
      arrival: result.connxLeg2Arrival || result.arrival,
      arrivalName: result.connxLeg2ArrivalName || result.arrivalName,
      arrivalPortName:
        result.connxLeg2ArrivalPortName || result.arrivalPortName,
      arrivalTerminal:
        result.connxLeg2ArrivalTerminal || result.arrivalTerminal,
      arrivalDate: result.connxLeg2ArrivalDate || result.arrivalDate,
      arrivalDateAsString:
        result.connxLeg2ArrivalDateAsString || result.arrivalDateAsString,
      arrivalTimeAsString:
        result.connxLeg2ArrivalTimeAsString || result.arrivalTimeAsString,
      classBusiness: result.connxLeg2ClassesBusiness,
      classEconomy: result.connxLeg2ClassesEconomy,
      classFirst: result.connxLeg2ClassesFirst,
      mealsBusiness: result.connxLeg2MealsBusiness,
      mealsEconomy: result.connxLeg2MealsEconomy,
      mealsFirst: result.connxLeg2MealsFirst,
      depart: result.connxLeg2Depart,
      departName: result.connxLeg2DepartName,
      departPortName: result.connxLeg2DepartPortName,
      departureTerminal: result.connxLeg2DepartureTerminal,
      departureDate: result.connxLeg2DepartureDate,
      departureDateAsString: result.connxLeg2DepartureDateAsString,
      departureTimeAsString: result.connxLeg2DepartureTimeAsString,
      effectiveFrom: result.effectiveFrom,
      effectiveTo: result.effectiveTo,
      flightNo: result.connxLeg2FlightNo,
      operatedBy: result.connxLeg2OperatedBy,
      travelTime: result.connxLeg2TravelTime,
      oagOpByFlightNo: result.connxLeg2OagOpByFlightNo,
      oagOpByCarrier: result.connxLeg2OagOpByCarrier,
      sadName: result.connxLeg2OagOpByCarrierName || result.connxLeg2SadName,
      frequency: result.connxLeg2Frequency,
    };
  } else if (leg === 3) {
    if (result.connxLeg3OperatedBy === null) return null;
    return {
      aircraftName: result.connxLeg3AircraftName,
      airline: result.connxLeg3Airline,
      arrival: result.connxLeg3Arrival || result.arrival,
      arrivalName: result.connxLeg3ArrivalName || result.arrivalName,
      arrivalPortName:
        result.connxLeg3ArrivalPortName || result.arrivalPortName,
      arrivalTerminal:
        result.connxLeg3ArrivalTerminal || result.arrivalTerminal,
      arrivalDate: result.arrivalDate,
      arrivalDateAsString: result.arrivalDateAsString,
      arrivalTimeAsString:
        result.connxLeg3ArrivalTimeAsString || result.arrivalTimeAsString,
      classBusiness: result.connxLeg3ClassesBusiness,
      classEconomy: result.connxLeg3ClassesEconomy,
      classFirst: result.connxLeg3ClassesFirst,
      mealsBusiness: result.connxLeg3MealsBusiness,
      mealsEconomy: result.connxLeg3MealsEconomy,
      mealsFirst: result.connxLeg3MealsFirst,
      depart: result.connxLeg3Depart,
      departName: result.connxLeg3DepartName,
      departPortName: result.connxLeg3DepartPortName,
      departureTerminal: result.connxLeg3DepartureTerminal,
      departureDate: result.connxLeg3DepartureDate,
      departureDateAsString: result.connxLeg3DepartureDateAsString,
      departureTimeAsString: result.connxLeg3DepartureTimeAsString,
      effectiveFrom: result.effectiveFrom,
      effectiveTo: result.effectiveTo,
      flightNo: result.connxLeg3FlightNo,
      operatedBy: result.connxLeg3OperatedBy,
      travelTime: result.connxLeg3TravelTime,
      oagOpByFlightNo: result.connxLeg3OagOpByFlightNo,
      oagOpByCarrier: result.connxLeg3OagOpByCarrier,
      sadName: result.connxLeg3OagOpByCarrierName || result.connxLeg3SadName,
      frequency: result.connxLeg3Frequency,
    };
  }
}

export default SearchFlightsResultModalDetails;
